import { useState } from "react";

import styles from "./CensusDrawer.module.css";
import { Formik, Form } from "formik";
import {
  SystemIcon,
  Button,
  Avatar,
  Badge,
} from "@justworkshr/milo-components";
import { getProspectUuid } from "utils/idHelpers";
import { ConvertToISODate } from "utils/helper";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  EmployeeCensusSourceEnum,
  useUpsertHealthInsEmployeeCensusMutation,
} from "types/generated/operations";
import Tooltip from "uicomponents/Tooltip";
import Alert from "uicomponents/Alert";
import TextInput from "uicomponents/TextInput";
import RadioInput from "uicomponents/RadioInput";
import DateInput from "uicomponents/DateInput";
import SelectInput from "uicomponents/SelectInput";
import { HealthCensusType } from "uicomponents/CensusTable/types";
import { getMemberUuid } from "utils/idHelpers";
import { stateOption } from "pages/WorkersCompApplcation/utils/selectInputOptions";
import { employeeCensusValidationSchema } from "./utils/validationSchema";
import { Label } from "@justworkshr/milo-form";

const stateCodeOptions = stateOption.map((state) => ({
  value: state.description,
  description: state.description,
}));

interface HealthCensusDrawerProps {
  isOpen: boolean;
  closeDrawer: (refetch?: boolean) => void;
  employeeData?: HealthCensusType;
}

const HealthCensusDrawer = ({
  employeeData,
  isOpen,
  closeDrawer,
}: HealthCensusDrawerProps) => {
  const [hasError, setError] = useState(false);

  const [
    upsertHealthEmployeeCensus,
    { loading: upsertHealthEmployeeCensusLoading },
  ] = useUpsertHealthInsEmployeeCensusMutation();

  const anyLoading = upsertHealthEmployeeCensusLoading;

  const openClass = isOpen ? styles.open : "";

  const isFirstUser = employeeData?.uuid === getMemberUuid();

  let formattedDate = employeeData?.dateOfBirth || "";
  if (formattedDate) {
    const dateSubstrings = formattedDate.split("-");
    formattedDate = `${dateSubstrings[1]}/${dateSubstrings[2]}/${dateSubstrings[0]}`;
  }
  let formatedGender = employeeData?.gender?.toLowerCase() || "";
  if (formatedGender) {
    formatedGender =
      formatedGender.charAt(0).toUpperCase() + formatedGender.slice(1);
  }

  let defaultIsWaiving: string | null | boolean | undefined =
    employeeData?.healthInsInfo?.isWaiving;
  if (defaultIsWaiving !== undefined && defaultIsWaiving !== null) {
    defaultIsWaiving = defaultIsWaiving ? "Yes" : "No";
  }

  let defaultEmployeeType: any = employeeData?.isCompanyOwner;

  if (defaultEmployeeType !== undefined && defaultEmployeeType !== null) {
    defaultEmployeeType = defaultEmployeeType ? "owner" : "employee";
  }

  const defaults = {
    firstName: employeeData?.firstName || "",
    lastName: employeeData?.lastName || "",
    participantType: employeeData?.isCompanyOwner ? "owner" : "employee",
    employeeType: defaultEmployeeType || "",
    isWaiving: defaultIsWaiving || "",
    homeZip: employeeData?.homeZip || "",
    state: employeeData?.homeState || "",
    dateOfBirth: formattedDate,
    gender: formatedGender,
  };

  const handleSubmission = async (values: any) => {
    try {
      const formattedEmployee = {
        gender: values.gender
          .toUpperCase()
          .replace(/-/g, "_") as GenderTypeEnum,
        firstName: values.firstName,
        lastName: values.lastName,
        zip: values.homeZip,
        state: values.state || null,
        participantType: ParticipantTypeEnum.Employee,
        dateOfBirth: ConvertToISODate(values.dateOfBirth),
        isCompanyOwner: values.employeeType === "owner" ? true : false,
        isWaiving: values.isWaiving === "Yes" ? true : false,
      };

      // @ts-ignore
      if (employeeData?.uuid) formattedEmployee.uuid = employeeData.uuid;

      await upsertHealthEmployeeCensus({
        variables: {
          prospectId: getProspectUuid(),
          employeeHealthCensus: formattedEmployee,
          source: EmployeeCensusSourceEnum.WwHiApp,
        },
      });

      closeDrawer(true);
    } catch {
      setError(true);
    }
  };

  let validationSchema: any = employeeCensusValidationSchema;

  const scrollToError = (fieldName: string) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className={`${styles.drawerWrapper} ${openClass}`}>
      <div className={styles.drawerContent}>
        <div className={styles.healthDrawerHeader}>
          <h1>Update {employeeData?.firstName}'s info</h1>
          <button
            className={styles.closeButton}
            onClick={() => closeDrawer()}
            aria-label="Close drawer"
            aria-roledescription="button"
          >
            <SystemIcon iconName="close" />
          </button>
        </div>
        <Formik
          initialValues={defaults}
          onSubmit={handleSubmission}
          validationSchema={validationSchema}
        >
          {({ errors, handleSubmit }) => (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const firstErrorFieldName = Object.keys(errors)[0];
                scrollToError(firstErrorFieldName);
                handleSubmit(e);
              }}
            >
              <div className={styles.drawerBody}>
                <div className={styles.healthDrawerEmployeeInfo}>
                  <Avatar
                    name={`${employeeData?.firstName} ${employeeData?.lastName}`}
                    size="md"
                  />
                  <div className={styles.healthEmployeeInfoRight}>
                    <h4>
                      {employeeData?.firstName} {employeeData?.lastName}
                      {isFirstUser && (
                        <Badge
                          className={styles.firstUserBadge}
                          emphasis="subtle"
                          role="neutral"
                          children={"You"}
                        />
                      )}
                    </h4>
                    <p>
                      {employeeData?.isCompanyOwner ? "Owner" : "Employee"}
                      {employeeData?.isCompanyOwner &&
                        (employeeData?.employeeInformation?.isPaid === "Paid"
                          ? " | Paid"
                          : " | Unpaid")}
                    </p>
                  </div>
                </div>
                <p>
                  These refers to information on most legal documents. The
                  carriers need it to provide the most accurate benefits.
                </p>
                <RadioInput
                  name="isWaiving"
                  options={["Yes", "No"]}
                  label="Are they planning on waiving health insurance coverage?"
                />

                <DateInput label="Date of birth" name="dateOfBirth" />
                <div className={styles.sexRadioGroup}>
                  <Tooltip tooltipText="Carriers limit us to these options, but we’re working to better reflect everyone.">
                    <Label className={styles.sexLabel}>
                      Sex
                      <span className={styles.sexInfoWrapper}>
                        <SystemIcon
                          iconName="info-circle"
                          size="extra-small"
                          className={styles.sexInfoIcon}
                        />
                      </span>
                    </Label>
                  </Tooltip>
                  <RadioInput
                    name="gender"
                    options={["Male", "Female"]}
                    label="Sex"
                    message="This refers to the sex on most legal documents. The carriers need it
                    to provide the most accurate benefits. It cannot be seen by anyone
                    at your company."
                  />
                </div>

                <TextInput
                  label="Zip"
                  className={`${styles.drawerInput} ${styles.halfInput}`}
                  name="homeZip"
                />

                <SelectInput
                  name="state"
                  className={`${styles.drawerInput} ${styles.halfInput}`}
                  options={stateCodeOptions}
                  label={"State"}
                  placeholder="Select..."
                />

                {hasError && (
                  <Alert message="Sorry, there was an error. Please try refreshing the page." />
                )}
                <Button
                  className={styles.saveHealthEmployeeButton}
                  color="brand"
                  loading={anyLoading}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default HealthCensusDrawer;
